// Re-export image utilities
export { loadImage, upscaleImage } from 'src/utils/ImageUtils';

import { PreviewConstants } from 'src/constants';
import type {
  CenterCropResult,
  FitWithinResult,
  FitWithinOptions,
  WrapTextOptions,
  WrapTextResult,
  ResolveTextPositionOptions,
} from 'src/utils/preview/types';

/**
 * Create a canvas element with specified dimensions
 *
 * @param width - Canvas width in pixels
 * @param height - Canvas height in pixels
 * @returns Canvas element ready for rendering
 *
 */
export const createCanvas = (width: number, height: number): HTMLCanvasElement => {
  const canvas = document.createElement(PreviewConstants.CANVAS.ELEMENT);
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

/**
 * Calculate center crop transform (cover fit)
 * Scales image to fill target area, crops overflow based on shiftFactor.
 */
export const calculateCenterCrop = (
  imageWidth: number,
  imageHeight: number,
  targetWidth: number,
  targetHeight: number,
  offsetX: number = 0,
  offsetY: number = 0,
  shiftFactor: number = PreviewConstants.CANVAS_UTILS.DEFAULT_SHIFT_FACTOR,
): CenterCropResult => {
  const scale = Math.max(targetWidth / imageWidth, targetHeight / imageHeight);
  const scaledWidth = imageWidth * scale;
  const scaledHeight = imageHeight * scale;

  const x = offsetX + (targetWidth - scaledWidth) * shiftFactor;
  const y = offsetY + (targetHeight - scaledHeight) / 2;

  return { x, y, width: scaledWidth, height: scaledHeight };
};

/**
 * Fit image within bounds (contain fit)
 * Scales to fit within bounds, maintains aspect ratio, never scales up.
 */
export const fitWithin = (
  imageWidth: number,
  imageHeight: number,
  maxWidth: number,
  maxHeight: number,
  options: FitWithinOptions,
): FitWithinResult => {
  const { positionX = 0, positionY = 0 } = options;

  const scale = Math.min(maxWidth / imageWidth, maxHeight / imageHeight, 1);
  const width = imageWidth * scale;
  const height = imageHeight * scale;

  return { x: positionX, y: positionY, width, height };
};

/**
 * Wrap text to fit within width constraints with line limit
 * Returns lines array and truncation flag.
 */
export const wrapText = (options: WrapTextOptions): WrapTextResult => {
  const { text, maxWidth, maxLines, ctx } = options;
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const metrics = ctx.measureText(testLine);

    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }

    if (lines.length >= maxLines) break;
  }

  if (currentLine && lines.length < maxLines) {
    lines.push(currentLine);
  }

  const isTruncated = lines.length >= maxLines && words.length > lines.join(' ').split(' ').length;

  return { lines, isTruncated };
};

/**
 * Resolve text position from numeric values
 * Returns position coordinates directly.
 */
export const resolveTextPosition = (
  options: ResolveTextPositionOptions,
): { x: number; y: number } => {
  const { positionX, positionY } = options;
  return { x: positionX, y: positionY };
};
